import { useState, useEffect } from 'react'
import { getApiBaseUrl } from '../../utils/apiConfig';
import { Folder, FileText, FileImage, FileCode, BookOpen } from 'lucide-react'
import { Button } from '../ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion'
import api from '../../services/api'
import { logger } from '../../utils/logger'

// Simple image thumbnail component
function ImageThumbnail({ file, conversationId }: { file: FileItem; conversationId?: string }) {
  const [thumbnailError, setThumbnailError] = useState(false)
  
  const getImageUrl = () => {
    const cleanPath = file.path.startsWith('./') ? file.path.slice(2) : file.path
    const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/')

    const token = localStorage.getItem('bearer_token')

    // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
    const baseUrl = getApiBaseUrl();

    let url = `${baseUrl}/filesystem/image/${encodedPath}`
    const params = []

    if (conversationId) {
      params.push(`conversationId=${conversationId}`)
    }

    if (token) {
      params.push(`token=${encodeURIComponent(token)}`)
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`
    }

    return url
  }

  if (thumbnailError) {
    return <FileImage className="h-4 w-4 text-gray-400" />
  }

  return (
    <img
      src={getImageUrl()}
      alt={file.name}
      className="h-4 w-4 object-cover rounded border"
      onError={() => setThumbnailError(true)}
      onLoad={() => setThumbnailError(false)}
    />
  )
}

interface FileItem {
  name: string
  path: string
  type: 'file' | 'directory'
  size?: number
  extension?: string
  children?: FileItem[]
}

interface FileBrowserProps {
  onFileSelect: (file: FileItem) => void
  selectedFile?: FileItem | null
  conversationId?: string
}

export function FileBrowser({ onFileSelect, selectedFile, conversationId }: FileBrowserProps) {
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadRootDirectory()
  }, [conversationId])

  // Helper function to browse with conversation context
  const browseWithConversationContext = async (path: string) => {
    const cleanPath = path === '.' ? '' : path
    const encodedPath = cleanPath ? encodeURIComponent(cleanPath) : ''
    const url = encodedPath ? `/filesystem/browse/${encodedPath}` : '/filesystem/browse/'
    
    // Add conversation context if available
    const params = conversationId ? `?conversationId=${conversationId}` : ''
    const fullUrl = url + params
    
    const response = await api.get(fullUrl)
    return response.data
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const loadRootDirectory = async () => {
    try {
      setLoading(true)
      setError(null)
      
      logger.debug('FileBrowser: Loading files for conversation:', conversationId)
      
      // Start by browsing the current working directory with conversation context
      const response = await browseWithConversationContext('.')
      
      logger.debug('FileBrowser: API response:', response)
      
      if (!response || !response.files) {
        throw new Error('Invalid response from filesystem API')
      }
      
      const rootFiles = response.files.map((file: any) => ({
        ...file,
        children: file.type === 'directory' ? [] : undefined
      }))
      
      logger.debug('FileBrowser: Processed files:', rootFiles)
      setFiles(rootFiles)
    } catch (err) {
      logger.error('FileBrowser: Error loading files:', err)
      setError(err instanceof Error ? err.message : 'Failed to load files')
    } finally {
      setLoading(false)
    }
  }

  const loadDirectoryChildren = async (dirPath: string): Promise<FileItem[]> => {
    try {
      const response = await browseWithConversationContext(dirPath)
      return response.files.map((file: any) => ({
        ...file,
        children: file.type === 'directory' ? [] : undefined
      }))
    } catch (err) {
      logger.error('Error loading directory:', err)
      return []
    }
  }

  const getFileIcon = (file: FileItem) => {
    if (file.type === 'directory') {
      return <Folder className="h-4 w-4 text-primary" />
    }
    
    const ext = file.extension?.toLowerCase()
    // Show image thumbnail for image files
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext || '')) {
      return <ImageThumbnail file={file} conversationId={conversationId} />
    }
    if (ext === 'md' || ext === 'markdown') {
      return <BookOpen className="h-4 w-4 text-primary" />
    }
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'css', 'html'].includes(ext || '')) {
      return <FileCode className="h-4 w-4 text-primary" />
    }
    return <FileText className="h-4 w-4 text-gray-500" />
  }

  const handleDirectoryExpand = async (item: FileItem) => {
    if (item.type === 'directory' && (!item.children || item.children.length === 0)) {
      const children = await loadDirectoryChildren(item.path)
      
      // Update the files state to include the loaded children
      setFiles(prevFiles => updateFileChildren(prevFiles, item.path, children))
    }
  }

  const updateFileChildren = (files: FileItem[], targetPath: string, children: FileItem[]): FileItem[] => {
    return files.map(file => {
      if (file.path === targetPath) {
        return { ...file, children }
      } else if (file.children) {
        return { ...file, children: updateFileChildren(file.children, targetPath, children) }
      }
      return file
    })
  }

  const renderFileTree = (items: FileItem[], level = 0) => {
    const paddingLeft = level * 16; // 16px per level for better hierarchy
    
    return items.map((item) => (
      <div key={item.path} style={{ paddingLeft: `${paddingLeft}px` }}>
        {item.type === 'directory' ? (
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value={item.path} className="border-none">
              <AccordionTrigger 
                className="py-1 px-2 hover:bg-white/60 rounded text-xs font-medium [&[data-state=open]>svg]:rotate-90 transition-colors"
                onClick={() => handleDirectoryExpand(item)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {getFileIcon(item)}
                  <span className="truncate text-gray-700">{item.name}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0">
                {item.children && renderFileTree(item.children, level + 1)}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className={`w-full justify-start py-1 px-2 h-auto text-xs font-normal hover:bg-white/60 rounded transition-colors ${
              selectedFile?.path === item.path ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-700'
            }`}
            onClick={() => onFileSelect(item)}
          >
            <div className="flex items-center justify-between w-full min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                {getFileIcon(item)}
                <span className="truncate">{item.name}</span>
              </div>
              {item.size && (
                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                  {formatFileSize(item.size)}
                </span>
              )}
            </div>
          </Button>
        )}
      </div>
    ))
  }

  if (loading) {
    return (
      <div className="h-full bg-gray-50 border-r">
        <div className="p-4 text-sm text-gray-600">
          Loading files...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full bg-gray-50 border-r">
        <div className="p-4 text-sm text-red-600">
          Error: {error}
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2" 
            onClick={loadRootDirectory}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (!files || files.length === 0) {
    return (
      <div className="h-full bg-gray-50 border-r">
        <div className="p-4 text-sm text-gray-600">
          No files found
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2 block" 
            onClick={loadRootDirectory}
          >
            Refresh
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-gray-50 border-r">
      <div className="p-2">
        <div className="text-xs font-medium text-gray-600 mb-2 px-2">FILES</div>
        <div className="space-y-0.5">
          {renderFileTree(files)}
        </div>
      </div>
    </div>
  )
}